import { generalMixin } from "@/utils/general-mixin";
import { mapGetters } from "vuex";

/**
 * @module articleMixin
 * @description Provides article permission management functionality
 * 
 * Article Lifecycle & Status:
 * - Draft: Default when created, editable by owner
 * - Pending for Approval: Submitted for review, still editable by owner
 * - Approved: Approved but not published, editable by Admin/Manager/Approver
 * - Active: Published and visible on website
 * - Rejected: Returned for revision, editable by owner
 * - Inactive/Archived: Hidden from website and back-office
 * 
 * Permission Rules:
 * - Admin: Full access to all articles
 * - Manager: Can view/edit all, but only archive own articles
 * - Officer: Can view all, but only edit/archive own articles
 * - Member (L3/L4): Can only manage own articles with allowed statuses
 * - Approver: Can only approve and reject articles
 */

// Define constants for article statuses and allowed actions
const ARTICLE_STATUSES = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING_FOR_APPROVAL',
  APPROVED: 'APPROVED',
  ACTIVE: 'ACTIVE',
  REJECTED: 'REJECTED',
  INACTIVE: 'INACTIVE'
};

const MEMBER_ALLOWED_STATUSES = [
  ARTICLE_STATUSES.ACTIVE,
  ARTICLE_STATUSES.INACTIVE,
  ARTICLE_STATUSES.PENDING,
  ARTICLE_STATUSES.DRAFT,
  ARTICLE_STATUSES.REJECTED
];

const APPROVER_NON_PENDING_STATUSES = [
  ARTICLE_STATUSES.APPROVED,
  ARTICLE_STATUSES.REJECTED,
  ARTICLE_STATUSES.ACTIVE,
  ARTICLE_STATUSES.INACTIVE
];

export const articleMixin = {
  mixins: [generalMixin],
  computed: {
    ...mapGetters(["user"]),
  },
  methods: {
    /**
     * Checks if current user can add a new article
     * @returns {boolean} True if user has permission to add articles
     */
    canAddArticle() {
      return this.isBOUser || this.isMemberThree || this.isMemberFour;
    },

    /**
     * Checks if current user can edit the specified article
     * @param {Object|null} article - The article to check edit permissions for
     * @returns {boolean} True if user has permission to edit the article
     */
    canEditArticle(article = null) {
      if (!article) return false;
      
      // Admin and Manager can edit all articles
      if (this.isAdmin || this.isManager) return true;
      
      // Officer can only edit own articles
      if (this.isOfficer) {
        return this.isOwner(article.user?.id);
      }
      
      // Members can only edit own articles with allowed statuses
      if (this.isMemberThree || this.isMemberFour) {
        return this.isOwner(article.user?.id) && MEMBER_ALLOWED_STATUSES.includes(article.status);
      }
      
      // Approvers have special rules
      if (this.isApprover) {
        // Can edit non-pending articles regardless of ownership
        return APPROVER_NON_PENDING_STATUSES.includes(article.status);
      }
      
      return false;
    },

    /**
     * Checks if current user can archive the specified article
     * @param {Object|null} article - The article to check archive permissions for
     * @returns {boolean} True if user has permission to archive the article
     */
    canArchiveArticle(article = null) {
      if (!article) return false;
      
      // Only Admin has full archive rights
      if (this.isAdmin) return true;
      
      // Manager and Officer can only archive own articles
      if (this.isManager || this.isOfficer) {
        return this.isOwner(article.user?.id);
      }
      
      // Members can only archive own articles with allowed statuses
      if (this.isMemberThree || this.isMemberFour) {
        return this.isOwner(article.user?.id) && MEMBER_ALLOWED_STATUSES.includes(article.status);
      }
      
      // Approvers have special rules
      if (this.isApprover) {
        return APPROVER_NON_PENDING_STATUSES.includes(article.status);
      }
      
      return false;
    },

    /**
     * Unified method to check user permissions for various article actions
     * @param {string} action - The action to check ('add', 'edit', 'archive')
     * @param {Object|null} article - The article to check permissions for
     * @returns {boolean} True if user has permission for the specified action
     */
    checkAccessRight(action, article = null) {
      switch (action) {
        case "add":
          return this.canAddArticle();
        case "edit":
          return this.canEditArticle(article);
        case "archive":
          return this.canArchiveArticle(article);
        default:
          return false;
      }
    },

    /**
     * Checks if current user is the owner of an article
     * @param {number|string|null} userId - The user ID to compare with current user
     * @returns {boolean} True if current user is the owner
     */
    isOwner(userId) {
      return userId == this.user.id;
    },
  },
  mounted() {},
};
